import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';
import { useAuthStore } from '../state/authStore.ts';

/**
 * Reusable competitor comparison page. Renders a three-column table:
 * EAA Proof vs the named competitor. Pages bid on `<competitor> alternative`
 * keywords in the Google Ads Tool campaign land here.
 *
 * Comparison data must be:
 *   - Publicly verifiable (pricing pages, marketing copy, documented limitations)
 *   - Updated regularly (competitors change pricing — set a quarterly reminder)
 *   - Free of disparagement / unfounded claims (defamation risk in EU)
 *
 * All claims about competitors below are sourced from their own marketing pages or
 * regulator filings as of 2026-05-26. If anything looks wrong, fix it — being right
 * matters more than being persuasive.
 */

type ComparisonRow = {
  /** Question / dimension being compared, displayed in the leftmost column. */
  dimension: string;
  /** EAA Proof's answer. Mark `pro: true` for the green-check styling. */
  ours: { text: string; pro?: boolean };
  /** Competitor's answer. Mark `con: true` to flag the dimension as a competitor weakness. */
  theirs: { text: string; con?: boolean };
};

export type CompetitorProfile = {
  /** Short brand name as it appears in marketing copy (e.g. "AccessiBe"). */
  name: string;
  /** Slug used in the URL and the canonical path (e.g. "accessibe"). */
  slug: string;
  /** Public homepage of the competitor for the "Visit" link. */
  url: string;
  /** One-sentence positioning of the competitor in their own words (or close to it). */
  tagline: string;
  /** 2-3 paragraph intro. Why a buyer might consider them, the key difference vs EAA Proof. */
  intro: string;
  /** Where EAA Proof wins vs this competitor — used in summary box. 3-5 bullets. */
  weWinAt: string[];
  /** Where the competitor is genuinely a better fit — honesty builds trust. 1-3 bullets. */
  theyWinAt: string[];
  /** Per-dimension comparison rows. Aim for 8-12. */
  rows: ComparisonRow[];
};

export default function CompetitorComparisonPage({ profile }: { profile: CompetitorProfile }) {
  useSeo({
    title: `EAA Proof vs ${profile.name}`,
    canonicalPath: `/vs/${profile.slug}`,
    description: `Side-by-side comparison: EAA Proof vs ${profile.name}. Pricing, what gets scanned, EU compliance reporting, remediation depth, and which one is the better fit for your situation.`,
  });

  const user = useAuthStore(s => s.user);
  const scanCtaTarget = user ? '/' : '/sign-in';

  return (
    <div className="page vs-page">
      <header className="vs-header">
        <p className="vs-eyebrow">Comparison</p>
        <h1>
          EAA Proof <span className="vs-vs">vs</span> {profile.name}
        </h1>
        <p className="vs-tagline">{profile.tagline}</p>
      </header>

      <section className="vs-intro">
        {profile.intro.split('\n\n').map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </section>

      <section className="vs-summary">
        <div className="vs-summary-col vs-summary-ours">
          <h2>Pick EAA Proof if</h2>
          <ul>
            {profile.weWinAt.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Link to="/pricing" className="cta">
            See EAA Proof pricing
          </Link>
        </div>
        <div className="vs-summary-col vs-summary-theirs">
          <h2>Pick {profile.name} if</h2>
          <ul>
            {profile.theyWinAt.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <a href={profile.url} target="_blank" rel="noopener noreferrer" className="muted small">
            Visit {profile.name} →
          </a>
        </div>
      </section>

      <section className="vs-table-wrap">
        <h2>Feature-by-feature</h2>
        <div className="vs-table-scroll">
          <table className="vs-table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">EAA Proof</th>
                <th scope="col">{profile.name}</th>
              </tr>
            </thead>
            <tbody>
              {profile.rows.map((row) => (
                <tr key={row.dimension}>
                  <th scope="row">{row.dimension}</th>
                  <td className={row.ours.pro ? 'vs-pro' : undefined}>
                    {row.ours.pro && <span aria-hidden="true">✓ </span>}
                    {row.ours.text}
                  </td>
                  <td className={row.theirs.con ? 'vs-con' : undefined}>
                    {row.theirs.con && <span aria-hidden="true">✗ </span>}
                    {row.theirs.text}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small vs-footnote">
          Last reviewed: 2026-05-26. Comparison data sourced from {profile.name}'s public
          pricing and product pages. If anything is out of date, email{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> and we'll correct it.
        </p>
      </section>

      <section className="vs-cta">
        <h2>Try the difference for €29</h2>
        <p>
          One scan, one full WCAG 2.1 AA report + EN 301 549 accessibility statement. No
          subscription required. 7-day money-back guarantee.
        </p>
        <Link to={scanCtaTarget} className="cta">
          Start a scan
        </Link>
        <p className="muted small">
          See a <Link to="/sample">sample report</Link> first — no sign-up needed.
        </p>
      </section>
    </div>
  );
}
